import React, { useState, useEffect } from "react";
import {
  ChevronRight,
  Star,
  Award,
  Shield,
  Truck,
  Heart,
  Wrench,
  Share2,
} from "lucide-react";
import useStore from "../../store/useStore";
import { trackViewContent } from "../../lib/pixel";
import { productUrl } from "../../lib/routes";
import {
  getAvailableInventory,
  isSizeInStock,
} from "../../utils/inventoryUtils";
import {
  getAvailableSizes,
  hasMultipleSizes as productHasMultipleSizes,
} from "../../utils/productUtils";

const ProductDetailPage = ({ product, onBack }) => {
  const [selectedImage, setSelectedImage] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState(null);
  const [sizeError, setSizeError] = useState(false);

  // Resizing state
  const [needsResizing, setNeedsResizing] = useState(false);
  const [resizingType, setResizingType] = useState("");
  const [customSize, setCustomSize] = useState("");

  const addToCart = useStore((state) => state.addToCart);
  const toggleWishlist = useStore((state) => state.toggleWishlist);
  const isInWishlist = useStore((state) => state.isInWishlist(product.id));
  const setCartOpen = useStore((state) => state.setCartOpen);
  const cart = useStore((state) => state.cart);
  const showToast = useStore((state) => state.showToast);

  const availableSizes = getAvailableSizes(product);
  const hasMultipleSizes = productHasMultipleSizes(product);

  const availableInventory = selectedSize
    ? getAvailableInventory(product, selectedSize, cart)
    : 0;

  // Check if product is a ring
  const isRing = product.subcategory === "Rings";

  // Calculate resizing fee
  const RESIZING_FEE = 5;
  const resizingFee = needsResizing && resizingType ? RESIZING_FEE : 0;
  const finalPrice = product.price + resizingFee;

  useEffect(() => {
    trackViewContent(product);
    // Once per product opened, not on every live catalog refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product.sku]);

  // Auto-select if only one size and it's in stock
  useEffect(() => {
    if (availableSizes.length === 1) {
      const singleSize = availableSizes[0];
      if (isSizeInStock(product, singleSize, cart)) {
        setSelectedSize(singleSize);
      }
    }
  }, [availableSizes, product, cart]);

  // Reset quantity when size changes
  useEffect(() => {
    setQuantity(1);
  }, [selectedSize]);

  // Limit quantity to available inventory
  useEffect(() => {
    if (quantity > availableInventory && availableInventory > 0) {
      setQuantity(availableInventory);
    }
  }, [quantity, availableInventory]);

  // Reset resizing when it's toggled off
  useEffect(() => {
    if (!needsResizing) {
      setResizingType("");
      setCustomSize("");
    }
  }, [needsResizing]);

  const handleAddToCart = () => {
    // Check if size selection is required
    if (hasMultipleSizes && !selectedSize) {
      setSizeError(true);
      return;
    }

    // Check inventory
    if (availableInventory <= 0) {
      alert("This item is out of stock!");
      return;
    }

    // Validate resizing inputs
    if (needsResizing) {
      if (!resizingType) {
        alert("Please select resizing type (Tighten or Widen)");
        return;
      }
      if (!customSize.trim()) {
        alert("Please enter your desired ring size");
        return;
      }
    }

    // Add product with complete resizing info
    for (let i = 0; i < quantity; i++) {
      addToCart(product, selectedSize, {
        needsResizing,
        resizingType,
        customSize,
        resizingFee,
      });
    }
    setQuantity(1);
    setCartOpen(true);
  };

  const handleSizeSelect = (size) => {
    setSelectedSize(size);
    setSizeError(false);
  };

  const isInCart =
    selectedSize &&
    cart.some(
      (item) => item.id === product.id && item.selectedSize === selectedSize,
    );

  // Phones get the native share sheet (WhatsApp, Instagram...); desktops
  // copy the link instead.
  const handleShare = async () => {
    const url = productUrl(product);
    if (navigator.share) {
      try {
        await navigator.share({ title: product.name, url });
      } catch (err) {
        // dismissed the share sheet
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      showToast("Link copied");
    } catch (err) {
      window.prompt("Copy this link:", url);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f5f3f9] to-[#e8e4f3]">
      <div className="flex items-center justify-between px-4 py-6 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <button
          onClick={onBack}
          className="flex items-center text-gray-600 transition hover:text-gray-900"
        >
          <ChevronRight className="w-5 h-5 rotate-180" />
          <span className="ml-2">Back to Shop</span>
        </button>
        <button
          onClick={handleShare}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 transition bg-white border border-gray-300 rounded-full hover:border-[#d4af37]"
        >
          <Share2 className="w-4 h-4" />
          Share
        </button>
      </div>

      <div className="px-4 py-8 mx-auto max-w-7xl sm:px-6 lg:px-8">
        <div className="overflow-hidden bg-white shadow-xl rounded-3xl">
          <div className="grid gap-12 p-8 md:grid-cols-2 lg:p-12">
            {/* Image Gallery */}
            <div>
              <div className="mb-4 overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 rounded-2xl">
                <img
                  src={product.images[selectedImage]}
                  alt={product.name}
                  className="object-contain w-full aspect-square"
                />
              </div>
              <div className="grid grid-cols-4 gap-3">
                {product.images.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`rounded-xl overflow-hidden border-2 transition ${
                      selectedImage === idx
                        ? "border-[#d4af37]"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <img
                      src={img}
                      alt=""
                      className="object-contain w-full aspect-square"
                    />
                  </button>
                ))}
              </div>
            </div>

            {/* Product Details */}
            <div className="flex flex-col">
              <span className="inline-block mb-3 px-4 py-1.5 bg-[#e8e4f3] text-[#1a1a3e] rounded-full text-sm font-semibold w-fit">
                {product.gender}
              </span>

              <h1 className="mb-4 font-serif text-4xl text-gray-900">
                {product.name}
              </h1>
              {product.sku && (
                <p className="mb-4 text-sm font-mono text-gray-500">
                  SKU: {product.sku}
                </p>
              )}

              {/* Rating */}
              <div className="flex items-center mb-6 space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-5 h-5 ${
                      i < product.rating
                        ? "text-[#d4af37] fill-current"
                        : "text-gray-300"
                    }`}
                  />
                ))}
                <span className="ml-2 font-medium text-gray-600">
                  ({product.reviews} reviews)
                </span>
              </div>

              {/* Price with resizing fee breakdown */}
              <div className="mb-8">
                <p className="text-5xl font-bold text-gray-900">
                  ${finalPrice.toLocaleString()}
                </p>
                {resizingFee > 0 && (
                  <p className="mt-2 text-sm text-gray-600">
                    Base price: ${product.price} + Resizing: ${resizingFee}
                  </p>
                )}
              </div>

              <p className="mb-8 text-lg leading-relaxed text-gray-700">
                {product.description}
              </p>

              {/* Size Selector */}
              {availableSizes.length > 0 && (
                <div className="mb-6">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">
                      Select Size{" "}
                      {hasMultipleSizes && (
                        <span className="text-red-500">*</span>
                      )}
                    </h3>
                    {selectedSize && (
                      <span className="text-sm text-gray-600">
                        Selected: {selectedSize}
                        {availableInventory === 0 && (
                          <span className="ml-2 text-red-500">
                            (Out of Stock)
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {availableSizes.map((size, index) => {
                      const sizeInStock = isSizeInStock(product, size, cart);

                      return (
                        <button
                          key={index}
                          onClick={() => sizeInStock && handleSizeSelect(size)}
                          disabled={!sizeInStock}
                          className={`px-4 py-2 border-2 rounded-lg font-medium transition relative ${
                            selectedSize === size
                              ? "border-[#d4af37] bg-[#fffbf0] text-[#d4af37]"
                              : sizeInStock
                                ? "border-gray-300 text-gray-700 hover:border-gray-400"
                                : "border-gray-200 bg-gray-100 text-gray-400 cursor-not-allowed"
                          }`}
                        >
                          {size}
                          {!sizeInStock && (
                            <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
                              Sold
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {sizeError && (
                    <p className="mt-2 text-sm text-red-600">
                      Please select a size before adding to cart
                    </p>
                  )}
                  {selectedSize && availableInventory === 0 && (
                    <p className="mt-2 text-sm text-red-600">
                      This size is currently out of stock
                    </p>
                  )}
                </div>
              )}

              {/* Ring Resizing Option (Only for Rings) */}
              {isRing && selectedSize && (
                <div className="p-4 mb-6 border-2 border-gray-200 rounded-xl">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Wrench className="w-5 h-5 text-[#d4af37]" />
                      <h3 className="font-semibold text-gray-900">
                        Ring Resizing Service
                      </h3>
                    </div>
                    <span className="text-sm font-semibold text-[#d4af37]">
                      +${RESIZING_FEE}
                    </span>
                  </div>

                  <label className="flex items-center gap-3 mb-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={needsResizing}
                      onChange={(e) => setNeedsResizing(e.target.checked)}
                      className="w-5 h-5 text-[#d4af37] focus:ring-[#d4af37] rounded"
                    />
                    <span className="text-sm text-gray-700">
                      I need this ring resized (${RESIZING_FEE} fee applies)
                    </span>
                  </label>

                  {needsResizing && (
                    <div className="pl-8 mt-4 space-y-4">
                      {/* Current Size Display */}
                      <div className="p-3 rounded-lg bg-gray-50">
                        <p className="mb-1 text-xs text-gray-600">
                          Current Ring Size:
                        </p>
                        <p className="font-semibold text-gray-900">
                          Size {selectedSize}
                        </p>
                      </div>

                      {/* Resizing Type */}
                      <div>
                        <p className="mb-3 text-sm font-medium text-gray-600">
                          Select resizing type: *
                        </p>
                        <div className="space-y-2">
                          <label
                            className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${
                              resizingType === "tighten"
                                ? "border-[#d4af37] bg-[#fffbf0]"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="resizingType"
                              value="tighten"
                              checked={resizingType === "tighten"}
                              onChange={(e) => setResizingType(e.target.value)}
                              className="w-4 h-4 text-[#d4af37] focus:ring-[#d4af37]"
                            />
                            <div>
                              <p className="font-medium text-gray-900">
                                Tighten (Make Smaller)
                              </p>
                              <p className="text-xs text-gray-600">
                                Reduce ring size
                              </p>
                            </div>
                          </label>

                          <label
                            className={`flex items-center gap-3 p-3 border-2 rounded-lg cursor-pointer transition ${
                              resizingType === "widen"
                                ? "border-[#d4af37] bg-[#fffbf0]"
                                : "border-gray-200 hover:border-gray-300"
                            }`}
                          >
                            <input
                              type="radio"
                              name="resizingType"
                              value="widen"
                              checked={resizingType === "widen"}
                              onChange={(e) => setResizingType(e.target.value)}
                              className="w-4 h-4 text-[#d4af37] focus:ring-[#d4af37]"
                            />
                            <div>
                              <p className="font-medium text-gray-900">
                                Widen (Make Larger)
                              </p>
                              <p className="text-xs text-gray-600">
                                Increase ring size
                              </p>
                            </div>
                          </label>
                        </div>
                      </div>

                      {/* Custom Size Input */}
                      <div>
                        <label className="block mb-2 text-sm font-medium text-gray-900">
                          Desired Ring Size: *
                        </label>
                        <input
                          type="text"
                          value={customSize}
                          onChange={(e) => setCustomSize(e.target.value)}
                          placeholder="e.g., 18, 19.5, 20"
                          className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#d4af37] focus:border-transparent"
                        />
                        <p className="mt-2 text-xs text-gray-500">
                          Enter the exact size you need (e.g., 17, 18.5, 20)
                        </p>
                      </div>

                      {/* Info Box */}
                      <div className="p-3 border border-blue-200 rounded-lg bg-blue-50">
                        <p className="text-xs text-blue-800">
                          <strong>Note:</strong> Our expert jewelers will resize
                          your ring from size <strong>{selectedSize}</strong> to
                          size <strong>{customSize || "___"}</strong>. This
                          process typically takes 3-5 business days.
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Features */}
              {product.features.length > 0 && (
                <div className="p-6 mb-8 bg-gray-50 rounded-xl">
                  <h3 className="mb-4 font-semibold text-gray-900">
                    Product Features
                  </h3>
                  <ul className="space-y-3">
                    {product.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center text-gray-700">
                        <div className="w-2 h-2 bg-[#d4af37] rounded-full mr-3"></div>
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Trust Badges */}
              <div className="grid grid-cols-3 gap-4 mb-8">
                <div className="p-4 text-center bg-gray-50 rounded-xl">
                  <Award className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
                  <p className="text-xs font-medium text-gray-600">
                    Certified Authentic
                  </p>
                </div>
                <div className="p-4 text-center bg-gray-50 rounded-xl">
                  <Shield className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
                  <p className="text-xs font-medium text-gray-600">
                    Lifetime Warranty
                  </p>
                </div>
                <div className="p-4 text-center bg-gray-50 rounded-xl">
                  <Truck className="w-8 h-8 text-[#d4af37] mx-auto mb-2" />
                  <p className="text-xs font-medium text-gray-600">
                    Safe Shipping
                  </p>
                </div>
              </div>

              {/* Quantity & Add to Cart */}
              <div className="flex gap-4 mb-6">
                {availableInventory > 0 && (
                  <div className="flex items-center gap-3 px-4 py-2 bg-gray-100 rounded-full">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="flex items-center justify-center w-8 h-8 font-semibold transition rounded-full hover:bg-white"
                    >
                      -
                    </button>
                    <span className="w-12 font-semibold text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() =>
                        setQuantity(Math.min(availableInventory, quantity + 1))
                      }
                      disabled={quantity >= availableInventory}
                      className="flex items-center justify-center w-8 h-8 font-semibold transition rounded-full hover:bg-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      +
                    </button>
                  </div>
                )}
                <button
                  onClick={handleAddToCart}
                  disabled={isInCart || availableInventory === 0}
                  className={`flex-1 py-4 rounded-full font-semibold text-lg transition shadow-xl hover:shadow-2xl ${
                    isInCart || availableInventory === 0
                      ? "bg-gray-300 text-gray-600 cursor-not-allowed"
                      : "bg-gradient-to-r from-[#1a1a3e] to-[#2a2a5e] text-white hover:from-[#2a2a5e] hover:to-[#1a1a3e]"
                  }`}
                >
                  {availableInventory === 0
                    ? "Out of Stock"
                    : isInCart
                      ? "In Cart"
                      : "Add to Cart"}
                </button>
              </div>

              {isInCart && availableInventory === 0 && (
                <p className="mb-4 text-sm text-center text-orange-600">
                  ✓ This item is in your cart (Last one!)
                </p>
              )}

              {/* Favorites Button */}
              <button
                onClick={() => toggleWishlist(product)}
                className={`w-full py-4 rounded-full font-semibold transition flex items-center justify-center gap-2 ${
                  isInWishlist
                    ? "bg-red-500 text-white hover:bg-red-600"
                    : "border-2 border-gray-300 text-gray-700 hover:border-gray-400"
                }`}
              >
                <Heart
                  className={`w-5 h-5 ${isInWishlist ? "fill-current" : ""}`}
                />
                {isInWishlist ? "Remove from Favorites" : "Add to Favorites"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetailPage;
